import './ApplicationOverview.scss';

import { ApplicationOverviewHeader } from './ApplicationOverviewHeader';
import { AppLogoMetadata } from '@security/system-overview';
import { createAsyncSelectors } from '@lib/selectors';
import { DetectedAppNotice } from './DetectedAppNotice';
import { GraphZoomButtons } from '@security/system-overview/GraphZoomButtons';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { SonarCard } from '@ui/general';
import { When } from 'react-if';

import {
	anchorPoints,
	buildEdge,
	buildImageMetadata,
	getTextWidth,
	OverviewGraph
} from '@security/system-overview/OverviewGraph';
import {
	coreActions,
	DataSecurityApplicationObject,
	DataSecurityApplicationReference,
	DataSecurityApplicationStatus,
	defaultForceOptions,
	emptyGraphData,
	FeatureFlag
} from '@sonar-software/sonar-core';
import {
	GraphConfigEditor,
	GraphLayouts,
	GraphLayoutSelector,
	GraphLines,
	GraphLineSelector
} from '@security/graph-tools';
import { InputNumber, Row, Space } from 'antd';
import { IUserEdge, IUserNode } from '@antv/graphin';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useApplicationReference, useAsyncStateWithPreload, useFeatureFlag } from '@hooks';

function buildImage(appId: number, app: DataSecurityApplicationReference): Promise<AppLogoMetadata> {
	return buildImageMetadata(appId, app.name, app.label);
}

function buildEdges(entries: Array<DataSecurityApplicationObject>, lineType: string): Array<IUserEdge> {
	return entries.map((entry) =>
		buildEdge(entry.read, entry.write, lineType, 'app', String(entry.sonarObjectId)));
}


function buildNodes(
	entries: Array<DataSecurityApplicationObject>,
	appMetadata: AppLogoMetadata
): Array<IUserNode> {
	const maxHeight = 56;

	let centerNode:IUserNode = {
		anchorPoints: anchorPoints,
		id: 'app'
	};

	if (appMetadata.path) {
		centerNode = {
			...centerNode,
			cursor: 'pointer',
			img: appMetadata.path,
			size: [ (appMetadata.width || 1) * (maxHeight / (appMetadata.height || maxHeight)), maxHeight ],
			type: 'app-center-display-image'
		};
	} else {
		centerNode = {
			...centerNode,
			label: appMetadata.name,
			labelCfg: {
				style: {
					background: {
						fill: '#FAFAFA',
						padding: [ 8, 6 ],
						radius: 2,
						stroke: '#D9D9D9'
					},
					cursor: 'pointer',
					fill: 'rgba(0,0,0,0.88)',
					font: 'inter',
					fontSize: 48
				}
			},
			size: [ getTextWidth(appMetadata.name, '48pt inter'), 80 ],
			style: {
				cursor: 'pointer',
				fill: '',
				stroke: ''
			},
			type: 'rect'
		};
	}

	return [
		 centerNode,
		...entries.map((entry) => {
			const nodeWidth = getTextWidth(entry.displayName, '21pt inter');

			return {
				id: String(entry.sonarObjectId),
				label: entry.displayName,
				size: [ nodeWidth, maxHeight ],
				type: 'app-object'
			};
		})
	];
}

export interface ApplicationOverviewProps {
	applicationId: number;
}

const appObjectsSelectors = createAsyncSelectors((state) => state.core.dataSecurity.applications.objects.get);

export const ApplicationOverview: React.FC<ApplicationOverviewProps> = memo(({ applicationId }) => {
	const displayTools = useFeatureFlag(FeatureFlag.DataSecurityGraphTools);

	const [ layoutType, setLayoutType ] = useState(GraphLayouts[ 7 ].value);
	const [ lineType, setLineType ] = useState(GraphLines[ 12 ].value);
	const [ layoutOptions, setLayoutOptions ] = useState<Record<string, unknown>>({
		...defaultForceOptions,
		type: layoutType });
	const [ mockAppCount, setMockAppCount ] = useState(0);
	const [ maxZoom, setMaxZoom ] = useState<number>(2);
	const [ minZoom, setMinZoom ] = useState<string>('0.4');

	const application = useApplicationReference(applicationId);

	const [ appMetadata, setAppMetadata ] = useState<AppLogoMetadata | null>(null);

	const { data, isReceived } = useAsyncStateWithPreload(
		appObjectsSelectors,
		coreActions.requestGetCoreDataSecurityApplicationsObjects,
		true,
		{ applicationId }
	);

	useEffect(() => {
		buildImage(applicationId, application).then((result) => {
			setAppMetadata(result);
		});
	}, [ application, applicationId ]);

	const isDetected = application.status === DataSecurityApplicationStatus.Detected;

	const graphData = useMemo(() => (appMetadata ? {
		combos: [],
		edges: buildEdges(data ?? [], lineType),
		nodes: buildNodes(data ?? [], appMetadata)
	} : emptyGraphData),
	[ appMetadata, data, lineType ]);

	const isReady = isReceived && data && appMetadata;

	return (
		<SonarCard
			id='application-overview-card'
			title={ <ApplicationOverviewHeader applicationId={ applicationId } /> }>
			<LoadingSpinner
				minDuration={ 1000 }
				opaque
				spinning={ !isReady }>
				<OverviewGraph
					data={ isReady ? graphData : emptyGraphData }
					layoutOptions={ layoutOptions }
					maxZoom={ maxZoom }
					minZoom={ Number(minZoom) }>
					<Space
						direction='vertical'
						style={ { width: '100%' } }>
						<Row
							align='bottom'
							justify='end'>
							<GraphZoomButtons />
						</Row>
					</Space>
				</OverviewGraph>
				<When condition={ displayTools }>
					<Space>
						<GraphLayoutSelector
							onChange={ (value) => {
								setLayoutOptions({
									...layoutOptions,
									type: value
								});
								setLayoutType(value);
							} }
							value={ layoutType } />
						<GraphLineSelector
							onChange={ setLineType }
							value={ lineType } />
						<InputNumber
							min={ 0 }
							onChange={ (value) => setMockAppCount(value ?? 0) }
							value={ mockAppCount } />
						<InputNumber
							min={ 0 }
							onChange={ (value) => setMaxZoom(value ?? 2) }
							value={ maxZoom } />
						<InputNumber
							min='0'
							onChange={ (value) => setMinZoom(value ?? '0.4') }
							step='0.1'
							stringMode
							value={ minZoom } />
					</Space>
					<GraphConfigEditor
						onChange={ setLayoutOptions }
						value={ layoutOptions } />
				</When>
			</LoadingSpinner>
			{ isDetected && <DetectedAppNotice applicationId={ applicationId } /> }
		</SonarCard>
	);
});
